import { Clipboard } from '@angular/cdk/clipboard';
import { PlatformLocation } from '@angular/common';
import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  ProductListConfigurationKeys,
  ProductListConfigurations,
  PRODUCT_LIST_NAMESPACE,
} from '@epicuro-next/constants/rule-engine/product-list-rules';
import { ShareLinkService } from '@epicuro-next/data/share-link';
import { List, Media } from '@epicuro-next/models/engagement';
import { ShareLinkParams } from '@epicuro-next/models/share-link';
import { APP_PRIVATE_PATHS } from '@epicuro-next/navigation/constants';
import { selectConfigurationValueFrom } from '@epicuro-next/platform/configuration';
import { EnvironmentService } from '@epicuro-next/platform/environment';
import { TargetService } from '@epicuro-next/platform/target';
import {
  selectSiteId,
  selectSubSiteId,
} from '@epicuro-next/state/session-state';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { isNonNullish } from '@epicuro-next/utilities';
import { select, Store } from '@ngrx/store';
import { EMPTY, of, Subject, tap, throttleTime } from 'rxjs';
import {
  catchError,
  filter,
  map,
  pluck,
  switchMap,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';

const errorMessage = $localize`:Lists|Share error message@@listShareErrorMessage:There was an error while trying to share your list. Please try again later`;
const cantGetShareLink = $localize`:Lists|Cant get sharable link message@@cantGetSharableLinkMessage:There was an error while creating preview link for your list. You can still share this link without a preview.`;
const copiedMessage = $localize`:Lists|List id copied@@listIdCopied:List link copied to clipboard.`;

/**
 * A directive to handle all share link logic of a list.
 * Link creation process gets its source from API. If it fails, it'll create a link to list without site id.
 * Once the link is created, it will try to share it via native share functionality.
 * If it fails, link will be copied to clipboard.
 */
@Directive({
  selector: '[epcShareableProductList]',
  exportAs: 'epcShareableProductList',
  standalone: false,
})
export class ShareableListDirective implements OnDestroy {
  private get listId() {
    return this.list?.id;
  }
  private destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private shareLinkService: ShareLinkService,
    private targetService: TargetService,
    private clipboard: Clipboard,
    private router: Router,
    private location: PlatformLocation,
    private environmentService: EnvironmentService,
    private elementRef: ElementRef,
  ) {
    this.subscribeShareFlow();
  }
  /**
   * Provide list id to handle link sharing
   */
  @Input('epcShareableProductList')
  public list: List;

  @Input('epcShareableProductListLookbookId')
  public lookbookId: string;

  private appDomain$ = this.environmentService.environmentSettings.pipe(
    pluck('appDomain'),
  );

  private enableUrlShortener$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        ProductListConfigurationKeys.EnableUrlShortener,
        ProductListConfigurations
      >(
        PRODUCT_LIST_NAMESPACE,
        ProductListConfigurationKeys.EnableUrlShortener,
        false,
      ),
    ),
    map(Boolean),
  );

  private shareLinkSiteIdParam$ = this.store.pipe(
    select(selectSiteId),
    filter(isNonNullish),
    withLatestFrom(
      this.store.pipe(select(selectSubSiteId)),
      this.enableUrlShortener$,
    ),
    takeUntil(this.destroy$),
  );

  private triggerShareFlow$ = new Subject<void>();

  @HostListener('click')
  public handleShare() {
    this.triggerShareFlow$.next();
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private showMessage(message: string) {
    this.store.dispatch(
      showSnackBarMessage({
        message,
        messageType: MessageType.INFO,
      }),
    );
  }

  private setDisabled(disabled: boolean) {
    if (this.elementRef.nativeElement.tagName.toLowerCase() === 'button') {
      this.elementRef.nativeElement.disabled = disabled;
    }
  }

  private subscribeShareFlow() {
    this.triggerShareFlow$
      .pipe(
        throttleTime(1500),
        tap(() => {
          this.setDisabled(true);
        }),
        withLatestFrom(this.shareLinkSiteIdParam$),
        map(([_, params]) => params),
        switchMap(([siteId, subSiteId, enableUrlShortener]) =>
          this.getShareMethod(siteId, subSiteId, enableUrlShortener).pipe(
            switchMap((url) =>
              enableUrlShortener ? this.urlShortener(url) : of(url),
            ),
            switchMap((url) => this.shareTarget(url)),
            catchError(() => {
              this.setDisabled(false);
              return this.handlePostListError();
            }),
            take(1),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(({ url, sharedNative }) => {
        if (!sharedNative) {
          const copyResult = this.clipboard.copy(url);
          this.showMessage(copyResult ? copiedMessage : errorMessage);
        }
        this.setDisabled(false);
      });
  }

  public getShareMethod(
    siteId: string,
    subSiteId: string | null = null,
    tiny = false,
  ) {
    const mainMedia = this.list?.mainMedia as Media;

    const params: ShareLinkParams = {
      siteId,
      subSiteId,
      tiny,
      name: this.list.name,
      parentId: this.lookbookId,
      id: this.listId,
      mainMedia,
    };

    return this.appDomain$.pipe(
      switchMap((appDomain) =>
        this.shareLinkService.getShareLink(params, appDomain),
      ),
    );
  }

  private handlePostListError() {
    try {
      const url = this.getListLinkWithoutPreview();
      const copyResult = this.clipboard.copy(url);
      this.showMessage(copyResult ? copiedMessage : errorMessage);
      this.showMessage(cantGetShareLink);
    } catch (e) {
      this.showMessage(errorMessage);
    }
    return EMPTY;
  }

  private shareTarget(url: string) {
    return this.targetService
      .shareTarget({ url })
      .pipe(map((sharedNative) => ({ sharedNative, url })));
  }

  private getListLinkWithoutPreview() {
    return this.getPathFromUrlTree([
      APP_PRIVATE_PATHS.productList.path,
      this.listId,
    ]);
  }

  private getPathFromUrlTree(path: string[]) {
    const urlTree = this.router.createUrlTree([
      '/',
      APP_PRIVATE_PATHS.browse.path,
      ...path,
    ]);
    const pathStr = this.router.serializeUrl(urlTree);
    return this.getFullPath(pathStr);
  }

  private getFullPath(path: string) {
    const base = this.location.getBaseHrefFromDOM().replace(/\//g, '');
    const href = this.location.href;
    return `${href.split(base)[0]}${base}${path}`;
  }

  private urlShortener(url: string) {
    const [link] = url.match(/\/lnk\/.+/g) ?? [];

    return this.appDomain$.pipe(
      map((domainName) => (domainName ? domainName + link : url)),
    );
  }
}
